import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, Menu } from 'antd';
import { useNavigate } from 'react-router-dom';
import { InfiniteScroll } from 'common/components';
import { CountBadge } from 'common/components/CountBadge';
import { EOrderDirection } from 'common/const/common.enum';
import { ESocketEvent } from 'common/const/socket.enum';
import { useSocketContext } from 'common/context';
import { DEFAULT_VALUE_0, LIST_LIMIT_0 } from 'common/config';
import { countFormatter, showSuccessMessage } from 'common/helpers';
import { Package } from 'common/svg';
import { ERoute } from 'common/const';
import { AuthHooks, UserHooks } from 'common/hooks';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ReactComponent as CollapseIcon } from 'app/assets/images/redesign/collapse.svg';
import { RequestDetailsGeneralTab } from 'entities/Request/components/Tabs/RequestDetailsGeneralTab';
import { ERequestDetailsTab } from 'entities/Request/Request.enum';
import { IDistributeRequestPayload, IRequest } from 'entities/Request/Request.models';
import { RequestDetailsUpdatesTab } from 'entities/Request/components/Tabs/RequestDetailsUpdatesTab';
import { RequestHistoryComment } from 'entities/RequestHistory/components/RequestHistoryComment';
import { calculateUnassignedInventoriesCount, getRequestStatus } from 'entities/Request/Request.helper';
import { RequestDistributionsModal } from 'entities/Request/components';
import { IFile } from 'entities/File/File.models';
import { downloadFile } from 'entities/File/File.slice';
import { getInventoryAssignees, selectInventoryAssignees, selectIsInventoryLoading } from 'entities/Inventory/Inventory.slice';
import {
  distributeRequest,
  getRequestDistributions,
  selectIsRequestLoading,
  selectRequestDistributions,
  selectRequestMembers,
} from 'entities/Request/Request.slice';
import {
  createRequestHistoryComment,
  getRequestHistoryList,
  getRequestHistoryStats,
  selectIsRequestHistoryLoading,
  selectRequestHistoryList,
  selectRequestHistoryStats,
  setRequestHistoryStats,
} from 'entities/RequestHistory/RequestHistory.slice';
import { selectAuth } from 'entities/Auth/Auth.slice';
import { selectCurrentUser } from 'entities/User/User.slice';

interface IComponentProps {
  open: boolean;
  request: IRequest;
  onClose: () => void;
}

export const RequestDetails: FC<IComponentProps> = ({ open, request, onClose }) => {
  const dispatch = useAppDispatch();
  // auth
  const auth = useAppSelector(selectAuth);
  // inventory
  const isInventoryLoading = useAppSelector(selectIsInventoryLoading);
  const inventoryAssignees = useAppSelector(selectInventoryAssignees);
  // request
  const isRequestLoading = useAppSelector(selectIsRequestLoading);
  const requestMembers = useAppSelector(selectRequestMembers);
  const requestDistributions = useAppSelector(selectRequestDistributions);
  // requestHistory
  const requestHistoryList = useAppSelector(selectRequestHistoryList);
  const isRequestHistoryLoading = useAppSelector(selectIsRequestHistoryLoading);
  const requestHistoryStats = useAppSelector(selectRequestHistoryStats);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [menuSelectedKey, setMenuSelectedKey] = useState<ERequestDetailsTab>(ERequestDetailsTab.Updates);
  const [commentBlockHeightPx, setCommentBlockHeightPx] = useState<number>(DEFAULT_VALUE_0);
  const [openRequestDistributionsModal, setOpenRequestDistributionsModal] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { socket } = useSocketContext();
  const { isSeller } = AuthHooks.useIsSeller();
  const { isAccountAdmin, isManager, isHeadOfDepartment } = UserHooks.useCurrentUserRole();

  const isHistoryTab = menuSelectedKey === ERequestDetailsTab.Updates;
  const unassignedInventoriesCount = calculateUnassignedInventoriesCount(requestDistributions);
  const { isDeliveredRequest } = getRequestStatus(request.status);
  const isRequestOwner = currentUser?.id === request.userId;
  const canDistributeRequest =
    isDeliveredRequest && isRequestOwner && !isSeller && (isAccountAdmin || isManager || isHeadOfDepartment);

  const scrollToEndOfHistoryList = () => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  };

  const handleDownloadFile = (file: IFile) => {
    dispatch(downloadFile({ id: file.id, name: file.name }));
  };

  const handleClose = () => {
    setMenuSelectedKey(ERequestDetailsTab.Updates);
    onClose();
  };

  const onSendComment = (comment: string, files: string[], onSuccess: () => void) => {
    dispatch(
      createRequestHistoryComment({
        requestId: request.id,
        comment,
        files: files.length ? files : undefined,
        onSuccess,
      }),
    );
  };

  useEffect(() => {
    if (canDistributeRequest) {
      dispatch(getRequestDistributions(request.id));
    }
  }, [canDistributeRequest]);

  useEffect(() => {
    const fetch = async () => {
      if (open) {
        if (isHistoryTab) {
          await dispatch(
            getRequestHistoryList({ requestId: request.id, orderDirection: EOrderDirection.ASC, limit: LIST_LIMIT_0 }),
          );
          dispatch(setRequestHistoryStats(null));
        } else {
          dispatch(getRequestHistoryStats({ requestId: request.id }));
        }
      }
    };

    fetch();
  }, [open, isHistoryTab]);

  const updateRequestHistoryBySocket = (value: any) => {
    if (request.id === value.requestId) {
      dispatch(getRequestHistoryList({ requestId: request.id, orderDirection: EOrderDirection.ASC, limit: LIST_LIMIT_0 }));
    }
  };

  const updateRequestHistoryStatsBySocket = (value: any) => {
    if (request.id === value.requestId) {
      dispatch(getRequestHistoryStats({ requestId: request.id }));
    }
  };

  useEffect(() => {
    if (open && socket) {
      if (isHistoryTab) {
        socket.on(ESocketEvent.RequestHistories, updateRequestHistoryBySocket);
      } else {
        socket.on(ESocketEvent.RequestHistories, updateRequestHistoryStatsBySocket);
      }
    }

    return () => {
      if (socket) {
        socket.off(ESocketEvent.RequestHistories, updateRequestHistoryBySocket);
        socket.off(ESocketEvent.RequestHistories, updateRequestHistoryStatsBySocket);
      }
    };
  }, [open, socket, isHistoryTab]);

  const openDistributionsModal = async () => {
    await dispatch(getInventoryAssignees({ limit: LIST_LIMIT_0 }));
    setOpenRequestDistributionsModal(true);
  };

  const closeDistributionsModal = () => {
    setOpenRequestDistributionsModal(false);
  };

  const handleCreateRequestDistribution = (payload: Omit<IDistributeRequestPayload, 'requestId'>) => {
    dispatch(
      distributeRequest({
        requestId: request.id,
        items: payload.items,
        onSuccess: () => {
          payload.onSuccess?.();

          const assignedCount = payload.items.reduce((acc, item) => acc + item.count, 0);

          showSuccessMessage(
            <div className="flex align-center justify-between">
              <span>
                {assignedCount === unassignedInventoriesCount
                  ? 'Все инструменты из заказа распределены.'
                  : `${assignedCount} инструментов из заказа распределены.`}
              </span>

              <div className="ff-semibold fs-xs color-bright-green cursor-pointer" onClick={() => navigate(ERoute.InventoryList)}>
                Перейти в учёт инструмента
              </div>
            </div>,
          );
        },
      }),
    );
  };

  return (
    <div className="request-details">
      <div className="request-details__header">
        <Button className="button-circle secondary" icon={<CollapseIcon />} onClick={handleClose} />
      </div>

      <div className="flex direction-column request-details__body">
        <InfiniteScroll>
          <div className="text-h1-drawers mb-11">Детали заказа</div>

          {canDistributeRequest && (
            <div className="mb-11">
              <div className="mb-5">
                <Button
                  className="button-lg primary w-full"
                  onClick={openDistributionsModal}
                  loading={isInventoryLoading}
                  disabled={!unassignedInventoriesCount}
                >
                  Распределить инструменты
                </Button>
              </div>

              <div className="flex align-center gap-3">
                <Package className="icon-package-dark-grey" />
                <span className="ff-regular fs-s">
                  {unassignedInventoriesCount
                    ? `${countFormatter(unassignedInventoriesCount)} не распределено`
                    : 'Все инструменты распределены'}
                </span>
              </div>
            </div>
          )}

          <Menu
            items={[
              {
                label: 'Общая информация',
                key: ERequestDetailsTab.General,
              },
              {
                key: ERequestDetailsTab.Updates,
                label: (
                  <>
                    Обновления
                    <CountBadge count={requestHistoryStats?.unread} />
                  </>
                ),
              },
            ]}
            onClick={(e) => setMenuSelectedKey(e.key as ERequestDetailsTab)}
            selectedKeys={[menuSelectedKey]}
            mode="horizontal"
          />

          {menuSelectedKey === ERequestDetailsTab.General && (
            <RequestDetailsGeneralTab request={request} members={requestMembers} downloadFile={handleDownloadFile} />
          )}

          {menuSelectedKey === ERequestDetailsTab.Updates && (
            <div ref={ref} className="request-history__wrapper">
              <RequestDetailsUpdatesTab
                historyList={requestHistoryList}
                marginBottomOffsetPx={commentBlockHeightPx}
                scrollToEndOfHistoryList={scrollToEndOfHistoryList}
                downloadFile={handleDownloadFile}
              />
            </div>
          )}
        </InfiniteScroll>
      </div>

      {menuSelectedKey === ERequestDetailsTab.Updates && (
        <RequestHistoryComment
          token={auth?.access.token}
          loading={isRequestHistoryLoading}
          onSend={onSendComment}
          setCommentBlockHeightPx={setCommentBlockHeightPx}
        />
      )}

      <RequestDistributionsModal
        open={openRequestDistributionsModal}
        loading={isRequestLoading}
        distributions={requestDistributions}
        assignees={inventoryAssignees}
        onCancel={closeDistributionsModal}
        onSubmit={handleCreateRequestDistribution}
      />
    </div>
  );
};
